import { creditKind, KIND_COLS } from './creditKind';
import { PRESETS_ANALYTIC, minusDaysRange, toStoredPeriod, fromStoredPeriod } from './period';

// The three credit statuses, as stored. These are exact DB enum values, not
// display labels — the app's Hebrew string literals ARE the enum (see
// CLAUDE.md), so these constants exist to stop the same literal being retyped
// across the credits screens, NOT to translate or normalize it. Anything
// rendered to the user still shows the raw value.
//
// `statusBadge()` in utils/api.js deliberately keeps its own literal keys: it
// is one lookup table spanning several unrelated vocabularies (doc statuses,
// supplier statuses, credit statuses), so importing one of them here would
// make that table half-symbolic and harder to read, not easier.
export const CREDIT_STATUS = {
  OPEN:      'פתוח',
  RECEIVED:  'התקבל',
  CANCELLED: 'בוטל',
};

// Sort accessors shared by both credits pages' desktop headers and mobile
// sort sheet. Dates are DD/MM/YYYY text — reversed to YYYYMMDD they sort
// lexically, the same trick DocumentsPage uses.
export const SORT_ACCESSORS = {
  supplier: c => c.supplier_name,
  date:     c => (c.doc_date ? c.doc_date.split('/').reverse().join('') : null),
  total:    c => (c.credit_total || 0),
};

// Partitions a (already status-scoped, sorted) credit list into the three
// kind groups for the stacked, per-group table — identical logic on both
// the open and history pages, since a group's column set and subtotal don't
// depend on which statuses are in play. Subtotals exclude cancelled (בוטל)
// credits — a cancelled credit represents money that will never move, so it
// shouldn't inflate a "how much" total even on the page where cancelled rows
// are shown.
export function buildCreditGroups(sortedCredits, vatMult) {
  const defs = [
    { key: 'qty',      label: 'זיכויי כמות' },
    { key: 'price',    label: 'זיכויי הפרש-מחיר' },
    { key: 'combined', label: 'משולב (כמות + הפרש מחיר)' },
  ];
  return defs.map(def => {
    const rows = sortedCredits.filter(c => creditKind(c) === def.key);
    const live = rows.filter(c => c.status !== CREDIT_STATUS.CANCELLED);
    return {
      ...def,
      cols: KIND_COLS[def.key],
      rows,
      // Rows the bulk-select action can act on. Only meaningful on the open
      // page (every row already status 'פתוח' there); the history page passes
      // selectMode={false} so this is simply unused.
      openIds: rows.filter(c => c.status === CREDIT_STATUS.OPEN).map(c => c.id),
      count: live.length,
      net:   live.reduce((s, c) => s + (c.credit_total || 0), 0),
      gross: live.reduce((s, c) => s + (c.credit_total || 0) * vatMult, 0),
    };
  }).filter(g => g.rows.length > 0);
}

// Persist ONLY the supplier (name) + date filters across navigation — for
// both credits routes, so clicking 👁 to view an invoice, or switching
// between פתוחים and היסטוריית זיכויים, keeps them. Scoped per user id so a
// user swap in the same tab doesn't inherit another's filters, and per
// restaurant (null = "all restaurants") because switching restaurant remounts
// the page on the SAME history entry — a user-only key carried one
// restaurant's period and supplier into the next. Shared by both pages via
// the same key: it's session+user+restaurant scoped, not route scoped, so no
// separate sync mechanism (like sharedDateRange.js) is needed.
export const filtersKey = (uid, rid) => `peak.creditsFilters.${uid ?? 'anon'}.${rid ?? 'all'}`;
export function readSavedCreditsFilters(uid, rid) {
  try { return JSON.parse(sessionStorage.getItem(filtersKey(uid, rid))) || {}; }
  catch { return {}; }
}

// The period, unlike the supplier name, is NOT sticky across visits: /credits
// always OPENS on the last 90 days (owner, 28/09/2026 — a range remembered from
// days earlier was hiding every credit opened since). The saved period is
// honoured only when:
//   - the user is back on the SAME history entry that saved it — Back from a
//     document, or a refresh (`locKey` = react-router's location.key); or
//   - they crossed between the two credits tabs: the tab links (and the
//     empty-state cross-links) navigate with `keepPeriodFrom: <source key>`,
//     and it only counts while the saved record is still the one that source
//     entry wrote. Router state lives on in the history entry, so a bare
//     "keep" flag would, on a Back days later, restore whatever period some
//     OTHER credits visit saved last.
// Arriving from the navbar or any other link is a new entry with a new key,
// so it gets the default.
//
// Stored via toStoredPeriod, so even a kept preset is re-evaluated to today.
export const creditsDefaultRange = () => minusDaysRange(90)();
// The PRESETS_ANALYTIC key that range matches — what "is the page on its
// default?" is asked against, so a default rolled forward still counts.
export const CREDITS_DEFAULT_PRESET = '90d';

// react-router keys the FIRST entry of every document load 'default' — which a
// fresh load from a bookmark or a typed URL gets too, so a 'default' match
// alone can't tell "refresh / Back to the first entry" from "new visit". It is
// trusted when this same page load wrote it (module memory survives SPA
// navigation, not a document load), or when the document load itself was a
// reload or a back/forward.
const keysWrittenThisLoad = new Set();
const documentLoadType = () => (
  typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
    ? performance.getEntriesByType('navigation')[0]?.type || ''
    : ''
);
function isSameEntry(savedKey, key) {
  if (!savedKey || savedKey !== key) return false;
  if (key !== 'default') return true;
  return keysWrittenThisLoad.has(key) || ['reload', 'back_forward'].includes(documentLoadType());
}

export function initialCreditsRange(saved, location) {
  const keepFrom = location?.state?.keepPeriodFrom;
  const returning = isSameEntry(saved.locKey, location?.key) || (keepFrom && saved.locKey === keepFrom);
  return (returning && fromStoredPeriod(saved.period, PRESETS_ANALYTIC)) || creditsDefaultRange();
}

// `presetKey` comes from usePresetPeriod — never re-derived here (see
// toStoredPeriod for why).
export function writeSavedCreditsFilters(uid, rid, { locKey, nameFilter, dateFrom, dateTo, presetKey }) {
  keysWrittenThisLoad.add(locKey);
  try {
    sessionStorage.setItem(filtersKey(uid, rid), JSON.stringify({
      locKey, nameFilter, period: toStoredPeriod(presetKey, dateFrom, dateTo),
    }));
  } catch (err) {
    // Storage disabled or full: the filters just won't survive navigation.
    console.warn('credits filters not saved', err);
  }
}
